import {type AxiosInstance} from "axios";
import {createApi} from "../api/createApi.ts";
import garageService from "./garageService.ts";
import type {UniqueNo} from "../model/core/UniqueNo.ts";
import type {SupportContext} from "../model/user/SupportContext.ts";

export interface GarageGroupDetails {
    garageGroupNo: UniqueNo;
    name: string;
    groupCustomerNo: string | null;
}

export interface GarageGroupPage {
    total: number;
    garageGroups: GarageGroupDetails[] | null;
}

export interface GarageGroupListRequest {
    fuzzy: string;
    page: number;
    size: number;
}

export interface SupportContextOption extends SupportContext {
    customerNo: string;
}

/** Lookups for entering a support context; always sent without a context (#56913). */
class SupportContextService {
    api: AxiosInstance;

    constructor () {
        this.api = createApi("/b4cockpit/");
    }

    async search (fuzzy: string): Promise<SupportContextOption[]> {
        const [garages, groups] = await Promise.all([
            garageService.getGarageLabels({fuzzy}, {skipSupportContext: true}),
            this.api.post<GarageGroupDetails[]>("/support-context/garage-groups", {fuzzy}, {skipSupportContext: true})
                .then(response => response.data),
        ]);
        return [
            ...groups.map(group => ({
                type: "group" as const,
                no: group.garageGroupNo.index,
                label: group.name,
                customerNo: group.groupCustomerNo ?? "",
            })),
            ...(garages.garageLabels ?? []).map(garage => ({
                type: "garage" as const,
                no: garage.garageNo.index,
                label: garage.name,
                customerNo: garage.customerNo,
            })),
        ];
    }

    async listGroups (request: GarageGroupListRequest): Promise<GarageGroupPage> {
        const response = await this.api.post<GarageGroupPage>("/support-context/garage-group-list", request, {skipSupportContext: true});
        return response.data;
    }
}

const supportContextService = new SupportContextService();

export default supportContextService;
